///
/// EarthBrowser
///
/// Created by Matt Giger on 10/29/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Flat-shaded unit meshes for the procedural satellite archetypes, built
/// once per GL context and shared by every satellite of that kind. Each
/// archetype is a single mesh (one draw call per satellite) in the
/// satellite's local frame as set by SatelliteNode: +X toward the Earth
/// (nadir), +Y along-track, +Z the orbit normal. Unit = ember dome radius,
/// so a bus of half-size 0.35 sits inside the dome and the wings reach
/// ~1.7 past it. Faces carry their own normals and a per-vertex shade
/// (1 = bus, ~0.3 = solar panel) so the silhouette reads as parts.
///

import { type cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type SatelliteArchetype } from "./satelliteArchetype"

type Vec = [number, number, number]
type Axis = 0 | 1 | 2

const cBus = 1.0
const cStrut = 0.8
const cPanel = 0.3
const cDish = 0.9

class FacetMesh implements Geometry {
	public position: VertexAttribute
	public normal: VertexAttribute
	public shade: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public available: boolean = true
	public bounds!: cube
	public boundingSphere!: sphere

	constructor(context: StovokorContext) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.shade = new VertexAttribute(context, 1)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal, this.shade])
	}

	/** Counter-clockwise when seen from outside */
	public triangle(a: Vec, b: Vec, c: Vec, shade: number): void {
		const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2]
		const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
		let nx = uy * vz - uz * vy
		let ny = uz * vx - ux * vz
		let nz = ux * vy - uy * vx
		const len = Math.hypot(nx, ny, nz) || 1
		nx /= len; ny /= len; nz /= len
		const base = this.position.count
		for (const p of [a, b, c]) {
			this.position.addPos(p[0], p[1], p[2])
			this.normal.add(nx, ny, nz)
			this.shade.add(shade)
		}
		this.indexBuffer.triangle(base, base + 1, base + 2)
	}

	public quad(a: Vec, b: Vec, c: Vec, d: Vec, shade: number): void {
		this.triangle(a, b, c, shade)
		this.triangle(a, c, d, shade)
	}

	/** Axis-aligned box centred at c with half-sizes h */
	public box(c: Vec, h: Vec, shade: number): void {
		const [cx, cy, cz] = c
		const [hx, hy, hz] = h
		const p = (sx: number, sy: number, sz: number): Vec => [cx + sx * hx, cy + sy * hy, cz + sz * hz]
		this.quad(p(1, -1, -1), p(1, 1, -1), p(1, 1, 1), p(1, -1, 1), shade)       // +x
		this.quad(p(-1, -1, 1), p(-1, 1, 1), p(-1, 1, -1), p(-1, -1, -1), shade)   // -x
		this.quad(p(-1, 1, -1), p(-1, 1, 1), p(1, 1, 1), p(1, 1, -1), shade)       // +y
		this.quad(p(-1, -1, 1), p(-1, -1, -1), p(1, -1, -1), p(1, -1, 1), shade)   // -y
		this.quad(p(-1, -1, 1), p(1, -1, 1), p(1, 1, 1), p(-1, 1, 1), shade)       // +z
		this.quad(p(1, -1, -1), p(-1, -1, -1), p(-1, 1, -1), p(1, 1, -1), shade)   // -z
	}

	/** Closed cylinder along `axis` from a0 to a1 with the given radius */
	public cylinder(axis: Axis, a0: number, a1: number, radius: number, shade: number, segments: number = 12): void {
		const point = (a: number, angle: number): Vec => place(axis, a, radius * Math.cos(angle), radius * Math.sin(angle))
		const cap0 = place(axis, a0, 0, 0)
		const cap1 = place(axis, a1, 0, 0)
		for (let i = 0; i < segments; i++) {
			const t0 = (i / segments) * Math.PI * 2
			const t1 = ((i + 1) / segments) * Math.PI * 2
			this.quad(point(a0, t0), point(a0, t1), point(a1, t1), point(a1, t0), shade)
			this.triangle(cap0, point(a0, t1), point(a0, t0), shade)
			this.triangle(cap1, point(a1, t0), point(a1, t1), shade)
		}
	}

	/**
	 * Shallow dish opening toward +axis: rim ring at `rimAt`, centre sunk to
	 * `centreAt`, both faces drawn so it reads from any side
	 */
	public dish(axis: Axis, rimAt: number, centreAt: number, radius: number, shade: number, segments: number = 16): void {
		const centre = place(axis, centreAt, 0, 0)
		for (let i = 0; i < segments; i++) {
			const t0 = (i / segments) * Math.PI * 2
			const t1 = ((i + 1) / segments) * Math.PI * 2
			const r0 = place(axis, rimAt, radius * Math.cos(t0), radius * Math.sin(t0))
			const r1 = place(axis, rimAt, radius * Math.cos(t1), radius * Math.sin(t1))
			this.triangle(centre, r0, r1, shade)
			this.triangle(centre, r1, r0, shade)
		}
	}

	public finish(): this {
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
		return this
	}

	public destroy(): void {
		// Shared for the life of the context
	}
}

/**
 * Put `a` on `axis` and (u, v) on the remaining two axes in the cyclic
 * order that keeps (axis, u, v) right-handed, so the winding in cylinder()
 * and dish() faces outward for every axis
 */
function place(axis: Axis, a: number, u: number, v: number): Vec {
	switch (axis) {
		case 0: return [a, u, v]
		case 1: return [v, a, u]
		default: return [u, v, a]
	}
}

/** Two solar wings along ±Z as thin plates facing ±X (nadir / zenith) */
function wings(m: FacetMesh, zFrom: number, zTo: number, halfY: number, yokeHalf: number = 0.05): void {
	const zMid = (zFrom + zTo) / 2
	const zHalf = (zTo - zFrom) / 2
	for (const side of [1, -1]) {
		m.box([0, 0, side * (zFrom + 0.3) / 2], [yokeHalf, yokeHalf, (zFrom - 0.3) / 2], cStrut)
		m.box([0, 0, side * zMid], [0.03, halfY, zHalf], cPanel)
	}
}

function buildWings(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	m.box([0, 0, 0], [0.35, 0.3, 0.3], cBus)
	// Nadir instrument deck
	m.box([0.45, 0, 0], [0.1, 0.18, 0.18], cStrut)
	wings(m, 0.45, 1.7, 0.28)
	return m.finish()
}

function buildDish(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	m.box([0, 0, 0], [0.35, 0.35, 0.35], cBus)
	wings(m, 0.5, 1.6, 0.3)
	// Feed strut from the bus to the dish, dish rim toward nadir
	m.box([0.55, 0, 0], [0.2, 0.05, 0.05], cStrut)
	m.dish(0, 1.0, 0.75, 0.7, cDish)
	return m.finish()
}

function buildFlat(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	// Flat chassis facing nadir, long along-track
	m.box([0, 0, 0], [0.07, 0.9, 0.5], cBus)
	// Phased-array patches on the Earth face
	m.box([0.09, 0.35, 0], [0.02, 0.3, 0.35], cStrut)
	m.box([0.09, -0.35, 0], [0.02, 0.3, 0.35], cStrut)
	// Single wing toward zenith (-X)
	m.box([-0.17, 0, 0], [0.1, 0.06, 0.06], cStrut)
	m.box([-1.05, 0, 0], [0.8, 0.45, 0.03], cPanel)
	return m.finish()
}

function buildNav(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	m.box([0, 0, 0], [0.35, 0.35, 0.35], cBus)
	wings(m, 0.5, 1.9, 0.3)
	// L-band antenna array: a flat plate on the nadir face
	m.box([0.42, 0, 0], [0.04, 0.45, 0.45], cDish)
	return m.finish()
}

function buildTube(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	m.cylinder(1, -1.2, 1.2, 0.3, cBus, 14)
	// Aperture door at the forward end
	m.cylinder(1, 1.2, 1.3, 0.32, cStrut, 14)
	wings(m, 0.4, 1.15, 0.5, 0.04)
	return m.finish()
}

function buildCube(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	// 3U stick along nadir with two deployed side panels
	m.box([0, 0, 0], [0.6, 0.2, 0.2], cBus)
	for (const side of [1, -1]) {
		m.box([-0.2, 0, side * 0.55], [0.4, 0.02, 0.33], cPanel)
	}
	return m.finish()
}

function buildTruss(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	// Main truss across the orbit (ISS P/S truss), modules along-track
	m.box([0, 0, 0], [0.08, 0.08, 1.9], cStrut)
	m.cylinder(1, -1.1, 1.1, 0.17, cBus, 10)
	m.cylinder(1, -0.3, 0.3, 0.26, cBus, 10)
	// Four pairs of large panels, each pair above and below the truss
	for (const z of [-1.6, -0.9, 0.9, 1.6]) {
		for (const side of [1, -1]) {
			m.box([0, side * 0.7, z], [0.03, 0.5, 0.25], cPanel)
		}
	}
	return m.finish()
}

function buildCylinder(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	m.cylinder(1, -1.0, 1.0, 0.25, cStrut, 10)
	return m.finish()
}

function buildShard(context: StovokorContext): FacetMesh {
	const m = new FacetMesh(context)
	// Irregular tetrahedron, about half a dome radius across
	const a: Vec = [0.45, 0.1, -0.2]
	const b: Vec = [-0.3, 0.4, 0.15]
	const c: Vec = [-0.2, -0.35, 0.3]
	const d: Vec = [0.05, -0.1, -0.45]
	m.triangle(a, b, c, cDish)
	m.triangle(a, c, d, cDish)
	m.triangle(a, d, b, cDish)
	m.triangle(b, d, c, cDish)
	return m.finish()
}

export type SatelliteMarkerMeshes = Record<SatelliteArchetype, Geometry>

const sharedMeshes = new WeakMap<StovokorContext, SatelliteMarkerMeshes>()

export function sharedSatelliteMarkerMeshes(context: StovokorContext): SatelliteMarkerMeshes {
	let meshes = sharedMeshes.get(context)
	if (!meshes) {
		meshes = {
			wings: buildWings(context),
			dish: buildDish(context),
			flat: buildFlat(context),
			nav: buildNav(context),
			tube: buildTube(context),
			cube: buildCube(context),
			truss: buildTruss(context),
			cylinder: buildCylinder(context),
			shard: buildShard(context),
		}
		sharedMeshes.set(context, meshes)
	}
	return meshes
}
